/**
 * DashboardLayout — the AB8 Beauty "back office" shell, styled after the Fynix
 * dashboard reference: a soft left sidebar with a pill-active nav, an account
 * card, and a top header with a search field + quick actions. Pink palette.
 *
 *   <DashboardLayout title="Home" subtitle="…">{children}</DashboardLayout>
 */
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthCtx } from '../../App';

const Icon = ({ d, filled }) => (
  <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'}
       stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);

const ICONS = {
  dashboard: <><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></>,
  insights:  <><path d="M12 3a9 9 0 1 0 9 9"/><path d="M12 3v9l6 3"/><circle cx="19" cy="5" r="2"/></>,
  orders:    <><path d="M9 5h6M9 5a2 2 0 0 1-2 2H5v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7h-2a2 2 0 0 1-2-2"/><path d="M9 12h6M9 16h4"/></>,
  wallet:    <><rect x="3" y="6" width="18" height="13" rx="2.5"/><path d="M3 10h18"/><circle cx="17" cy="14" r="1.2" fill="currentColor" stroke="none"/></>,
  invoices:  <><path d="M7 3h7l5 5v13H7z"/><path d="M14 3v5h5M10 13h6M10 17h6"/></>,
  reports:   <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/></>,
  settings:  <><circle cx="12" cy="12" r="3.2"/><path d="M19.4 13.5a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-2.9-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0-1.2-2.9H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.2-2.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 2.9-1.2V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9z"/></>,
  help:      <><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.7M12 17h.01"/></>,
};

const MAIN = [
  { label: 'Dashboard',    to: '/dashboard',  icon: 'dashboard' },
  { label: 'AB8 Insights', to: '/affiliate',  icon: 'insights' },
  { label: 'Orders',       to: '/account',    icon: 'orders' },
  { label: 'My Wallet',    to: '/affiliate',  icon: 'wallet' },
  { label: 'Shop',         to: '/shop',       icon: 'invoices' },
  { label: 'Reports',      to: '/affiliate',  icon: 'reports' },
];
const PREF = [
  { label: 'Settings',    to: '/account', icon: 'settings' },
  { label: 'Help Center', to: '/support', icon: 'help' },
];

function NavItem({ item, active }) {
  return (
    <Link
      to={item.to}
      className={
        'flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ' +
        (active
          ? 'bg-white text-tea-700 shadow-[0_4px_14px_rgba(190,24,93,0.10)]'
          : 'text-gray-500 hover:text-tea-700 hover:bg-white/60')
      }
    >
      <span className={active ? 'text-tea-600' : 'text-gray-400'}>
        <Icon d={ICONS[item.icon]} filled={false} />
      </span>
      {item.label}
    </Link>
  );
}

export default function DashboardLayout({ title, subtitle, children }) {
  const { user, logout } = useAuthCtx();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials = `${(user?.firstName || 'A').charAt(0)}${(user?.lastName || '').charAt(0)}`.toUpperCase();
  const isActive = (to) => pathname === to || (to !== '/dashboard' && pathname.startsWith(to) && to !== '/shop');

  const Sidebar = (
    <aside className="w-64 shrink-0 bg-tea-50/60 border-r border-tea-100/80 flex flex-col h-full">
      {/* Brand */}
      <div className="px-6 py-5 flex items-center gap-2">
        <span className="grid place-items-center h-9 w-9 rounded-xl bg-gradient-to-br from-tea-500 to-plum-600 text-white">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
            <path d="M12 2c2 4 6 5 6 9a6 6 0 0 1-12 0c0-4 4-5 6-9z" />
          </svg>
        </span>
        <span className="text-lg font-extrabold tracking-tight text-gray-900">AB8 Beauty</span>
      </div>

      {/* Account switcher */}
      <div className="px-4">
        <div className="flex items-center gap-3 rounded-2xl bg-white px-3 py-2.5 shadow-[0_4px_14px_rgba(190,24,93,0.06)]">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-gradient-to-br from-tea-400 to-plum-600 text-white text-sm font-bold">
            {initials}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">{user?.firstName} {user?.lastName}</div>
            <div className="text-[11px] text-gray-400 capitalize">{user?.role || 'Personal'} Account</div>
          </div>
          <svg className="ml-auto h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
        </div>
      </div>

      {/* Nav */}
      <nav className="px-4 mt-6 flex-1 overflow-y-auto">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400 px-2 mb-2">Main menu</div>
        <div className="space-y-1">
          {MAIN.map((it, i) => <NavItem key={i} item={it} active={isActive(it.to)} />)}
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400 px-2 mt-7 mb-2">Preference</div>
        <div className="space-y-1">
          {PREF.map((it, i) => <NavItem key={i} item={it} active={false} />)}
        </div>
      </nav>

      <div className="p-4">
        <button onClick={() => { logout(); navigate('/'); }}
          className="w-full text-sm font-semibold text-gray-500 hover:text-tea-700 rounded-xl py-2.5 hover:bg-white/70 transition-colors">
          Log out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#f6f4f5] flex">
      {/* Desktop sidebar */}
      <div className="hidden lg:block sticky top-0 h-screen">{Sidebar}</div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <button className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} aria-label="Close menu" />
          <div className="relative h-full">{Sidebar}</div>
        </div>
      )}

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#f6f4f5]/80 backdrop-blur px-4 sm:px-8 py-5 flex items-center gap-4">
          <button className="lg:hidden grid place-items-center h-10 w-10 rounded-xl bg-white shadow-sm"
            onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <svg className="h-5 w-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-extrabold text-gray-900 leading-tight">{title}</h1>
            {subtitle && <p className="text-sm text-gray-400 italic truncate">"{subtitle}"</p>}
          </div>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 bg-white rounded-full px-4 py-2.5 shadow-[0_4px_14px_rgba(190,24,93,0.06)] w-72">
              <svg className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
              <input className="bg-transparent text-sm outline-none flex-1 placeholder:text-gray-400" placeholder="Search anything" />
              <span className="text-[10px] font-semibold text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">⌘F</span>
            </div>
            <button className="grid place-items-center h-10 w-10 rounded-full bg-white shadow-sm text-gray-500 hover:text-tea-700" aria-label="Settings">
              <Icon d={ICONS.settings} />
            </button>
            <span className="grid place-items-center h-10 w-10 rounded-full bg-gradient-to-br from-tea-400 to-plum-600 text-white text-sm font-bold">
              {initials}
            </span>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-8 pb-12">{children}</main>
      </div>
    </div>
  );
}
